/**
 * The theme token contract.
 *
 * A theme is data: a flat map of CSS custom-property values. Applying one
 * writes those values as inline styles on <html>, which override the defaults
 * baked into :root in index.css. Nothing else in the app knows a theme exists
 * — components only ever reference semantic utilities (bg-card, text-danger-ink),
 * so every surface recolors at once and none of them can drift.
 *
 * All values are oklch(). That matters: oklch lightness is perceptually
 * uniform, so holding L constant across hues keeps a chip row visually even,
 * and contrast can be reasoned about from the L value alone rather than
 * measured per swatch.
 */

/** The theme's tone families: the five status tones (lib/status.ts). */
export const TONE_NAMES = [
  'danger',
  'warning',
  'success',
  'info',
  'neutral',
] as const;

export type ToneName = (typeof TONE_NAMES)[number];

/** Per-tone slots. See index.css for what each is for. */
export interface ToneSlots {
  /** Solid mid-hue: solid fills, and /10 /15 /30 tint washes. */
  base: string;
  /** Tone-colored text on the app background. Target >= 4.5:1. */
  ink: string;
  /** Opaque pale wash for chip backgrounds. */
  surface: string;
  /** Hairline border pairing with `surface`. */
  line: string;
  /** Text sitting on top of `base`. */
  on: string;
}

/** One categorical series slot: a chip's wash, its text, and its hairline. */
export interface SeriesSlots {
  surface: string;
  ink: string;
  line: string;
}

/** The core shadcn surface//text tokens, plus the small-screen nav sheet set. */
export interface CoreTokens {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
  /** The logo's cherry. */
  brandCherry: string;
  /** The logo's stem. */
  brandStem: string;
  /** Chart series 1-5. */
  chart: [string, string, string, string, string];
}

export interface Theme {
  id: string;
  /** Display name in the picker. */
  name: string;
  /** One line on what the scheme is going for. */
  description: string;
  /**
   * Whether content surfaces are light or dark. Drives the `theme-color` meta
   * tag choice and lets the picker group schemes.
   */
  mode: 'light' | 'dark';
  core: CoreTokens;
  tones: Record<ToneName, ToneSlots>;
  /** Categorical series, `--series-N-*` from 1. See buildSeries.ts. */
  series: SeriesSlots[];
  /** Border radius scale root. Defaults to 0.75rem when omitted. */
  radius?: string;
  /**
   * Color the OS paints browser/PWA chrome with. iOS 26 samples the page
   * rather than reading the meta tag, but Android and desktop still use it.
   */
  chromeColor: string;
}

/** camelCase token key -> the CSS custom property it maps to. */
const CORE_VAR: Record<Exclude<keyof CoreTokens, 'chart'>, string> = {
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  cardForeground: '--card-foreground',
  popover: '--popover',
  popoverForeground: '--popover-foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  destructive: '--destructive',
  destructiveForeground: '--destructive-foreground',
  border: '--border',
  input: '--input',
  ring: '--ring',
  sidebar: '--sidebar',
  sidebarForeground: '--sidebar-foreground',
  sidebarPrimary: '--sidebar-primary',
  sidebarPrimaryForeground: '--sidebar-primary-foreground',
  sidebarAccent: '--sidebar-accent',
  sidebarAccentForeground: '--sidebar-accent-foreground',
  sidebarBorder: '--sidebar-border',
  sidebarRing: '--sidebar-ring',
  brandCherry: '--brand-cherry',
  brandStem: '--brand-stem',
};

/** Flatten a theme into the `--var: value` pairs that go on <html>. */
export function themeToVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [key, cssVar] of Object.entries(CORE_VAR)) {
    vars[cssVar] = theme.core[key as keyof typeof CORE_VAR];
  }

  theme.core.chart.forEach((value, i) => {
    vars[`--chart-${i + 1}`] = value;
  });

  for (const tone of TONE_NAMES) {
    const slots = theme.tones[tone];
    vars[`--tone-${tone}`] = slots.base;
    vars[`--tone-${tone}-ink`] = slots.ink;
    vars[`--tone-${tone}-surface`] = slots.surface;
    vars[`--tone-${tone}-line`] = slots.line;
    vars[`--tone-${tone}-on`] = slots.on;
  }

  // No :root defaults for these: nothing that wears a series renders before
  // the theme is applied at startup.
  theme.series.forEach((slots, i) => {
    vars[`--series-${i + 1}-surface`] = slots.surface;
    vars[`--series-${i + 1}-ink`] = slots.ink;
    vars[`--series-${i + 1}-line`] = slots.line;
  });

  vars['--radius'] = theme.radius ?? '0.75rem';

  return vars;
}

